/**
 * Vertical-aware portal wiring. School, coaching institute, and study centre
 * are distinct products (see `phase-P-portal-experience.md` §4), so the shell
 * renders a different primary-nav and home surface per vertical. Capability
 * gating reads `institution.capabilities` rather than hard-coding modules.
 */
import {
  Armchair,
  Bell,
  Brain,
  CalendarDays,
  ClipboardList,
  Home,
  LifeBuoy,
  User,
  Users,
} from "lucide-react";
import type { NavItem } from "../components/BottomNav";
import type { PortalStrings } from "./i18n/usePortalLocale";
import type { Institution, PortalCapability, Vertical } from "./institution";

/** Student primary nav per vertical. */
const STUDENT_NAV: Record<Vertical, NavItem[]> = {
  school: [
    { id: "home", label: "Home", icon: Home },
    { id: "schedule", label: "Schedule", icon: CalendarDays },
    { id: "notices", label: "Notices", icon: Bell },
    { id: "profile", label: "Profile", icon: User },
  ],
  coaching_institute: [
    { id: "home", label: "Home", icon: Home },
    { id: "tests", label: "Tests", icon: ClipboardList },
    { id: "notices", label: "Notices", icon: Bell },
    { id: "profile", label: "Profile", icon: User },
  ],
  study_centre: [
    { id: "home", label: "Home", icon: Home },
    { id: "seat", label: "Seat", icon: Armchair },
    { id: "revise", label: "Learn", icon: Brain },
    { id: "groups", label: "Groups", icon: Users },
    { id: "support", label: "Support", icon: LifeBuoy },
    { id: "profile", label: "Profile", icon: User },
  ],
};

/** Guardian nav — consistent across verticals (minimal for SC adults). */
const GUARDIAN_NAV: NavItem[] = [
  { id: "home", label: "Home", icon: Home },
  { id: "updates", label: "Updates", icon: Bell },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "profile", label: "Profile", icon: User },
];

export function studentNav(vertical: Vertical, learnerAppOffered = true): NavItem[] {
  const items = STUDENT_NAV[vertical];
  if (vertical === "study_centre" && !learnerAppOffered) {
    return items.filter((item) => item.id !== "revise");
  }
  return items;
}

/** Study-centre nav with localized labels (SC-17). */
export function localizedStudyCentreNav(
  strings: Pick<
    PortalStrings,
    "navHome" | "navSeat" | "navLearn" | "navGroups" | "navSupport" | "navProfile"
  >,
  learnerAppOffered = true,
): NavItem[] {
  const items: NavItem[] = [
    { id: "home", label: strings.navHome, icon: Home },
    { id: "seat", label: strings.navSeat, icon: Armchair },
    { id: "revise", label: strings.navLearn, icon: Brain },
    { id: "groups", label: strings.navGroups, icon: Users },
    { id: "support", label: strings.navSupport, icon: LifeBuoy },
    { id: "profile", label: strings.navProfile, icon: User },
  ];
  if (!learnerAppOffered) {
    return items.filter((item) => item.id !== "revise");
  }
  return items;
}

export function guardianNav(): NavItem[] {
  return GUARDIAN_NAV;
}

export function hasCapability(inst: Institution, cap: PortalCapability): boolean {
  return inst.capabilities.includes(cap);
}
